
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { getVisaTypeColor, getVisaTypeLabel } from './VisaTypeUtils';
import { Globe, Clock, FileText, Flag } from 'lucide-react';

interface VisaRequirementCardProps {
  id: number;
  destinationName: string;
  destinationCode: string;
  visaType: 'visa_free' | 'visa_on_arrival' | 'e_visa' | 'visa_required';
  maxStayDays?: number | null;
  notes?: string | null;
  region?: string | null;
}

export const VisaRequirementCard = ({
  id,
  destinationName,
  destinationCode,
  visaType,
  maxStayDays,
  notes,
  region,
}: VisaRequirementCardProps) => {
  // Get lowercase country code for flag emoji
  const countryCode = destinationCode.toLowerCase();
  
  return (
    <Card key={id} className="overflow-hidden hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="mr-3 text-2xl">
              {countryCode ? (
                <img
                  src={`https://flagcdn.com/48x36/${countryCode}.png`}
                  width="32"
                  height="24"
                  className="rounded shadow-sm"
                  alt={`${destinationName} flag`}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.style.display = 'none';
                    const parent = target.parentNode as HTMLElement;
                    if (parent) {
                      const flagIcon = document.createElement('div');
                      flagIcon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path><line x1="4" y1="22" x2="4" y2="15"></line></svg>';
                      flagIcon.className = 'text-gray-400';
                      parent.appendChild(flagIcon);
                    }
                  }}
                />
              ) : (
                <Flag className="h-6 w-6 text-gray-400" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold">{destinationName}</h3>
              {region && (
                <div className="flex items-center text-xs text-gray-500 mt-0.5">
                  <Globe className="h-3 w-3 mr-1" />
                  <span>{region}</span>
                </div>
              )}
            </div>
          </div>
          <span className={`font-medium rounded-full px-3 py-1 text-sm ${getVisaTypeColor(visaType)}`}>
            {getVisaTypeLabel(visaType)}
          </span>
        </div>
        
        <div className="mt-4 space-y-2">
          {maxStayDays && maxStayDays > 0 && (
            <div className="flex items-center text-sm text-gray-600">
              <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>Maximum stay: <span className="font-medium">{maxStayDays} days</span></span>
            </div>
          )}
          
          {notes && (
            <div className="flex items-start text-sm text-gray-600 mt-2">
              <FileText className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
              <span>{notes}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
